import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { entries } from 'lodash';
import { Text, Flex, Input, Tag, useToast, Box } from '@chakra-ui/react';

import { TField } from '../../form/Form';
import { TState, useActions } from '../../../redux/store';
import Modal from '../../modal/Modal';
import RSelect from '../../form/RSelect';
import FormGroup from '../../form/FormGroup';
import { allIcons, CirlceIcon } from '../../utils/Icon';
import { useGetSelect } from '../../../redux/actions';
import {
  addActionsList,
  deleteActionsList,
  updateActionsList,
} from '../../../api/tabsApi';
import { usePageTree } from './ManageDocuments';
import { useRolesSelect } from './ManageTabs';

const EMPTY_ACTION = { label: '', name_id: '', value: '' };

const Action = ({
  idx,
  list,
  setList,
}: {
  idx: number;
  list: {
    name_id: string;
    value: any;
  }[];
  setList: React.Dispatch<
    React.SetStateAction<
      {
        name_id: string;
        value: any;
      }[]
    >
  >;
}) => {
  const dataInputs = useSelector((state: TState) => state.dataInputs);

  const getInput = useGetSelect();
  const currInput = getInput(list[idx].name_id, list[idx].value);

  const isMulti = /multi/.test(currInput?.type || '');

  const inputsOptions = dataInputs
    .filter((input) => /user|select|date/.test(input.type))
    .map((input) => ({
      label: input.label || input.name_id,
      value: input.name_id,
    }));

  return (
    <Flex gap={3} mb={3} alignItems='center'>
      <RSelect
        options={inputsOptions}
        onChange={(v) => {
          const curr = getInput(v?.value);
          const isDate = /date/.test(curr?.type || '');

          setList((c) => {
            const newList = [...c];
            newList[idx] = {
              name_id: v?.value,
              value: isDate ? 'dateNow' : '',
            };
            return newList;
          });
        }}
        value={inputsOptions.find((opt) => opt.value === list[idx].name_id)}
      />
      {/user|select/.test(currInput?.type || '') && (
        <RSelect
          isMulti={isMulti}
          options={currInput?.options}
          onChange={(v) => {
            setList((c) => {
              const newList = [...c];
              newList[idx] = {
                ...newList[idx],
                value: isMulti ? v.map((val: any) => val.value) : v.value,
              };
              return newList;
            });
          }}
          value={currInput?.currOption || null}
        />
      )}
      {/date/.test(currInput?.type || '') && (
        <Text w='50%' whiteSpace='nowrap'>
          <Tag>Date du jour</Tag>
        </Text>
      )}
      <CirlceIcon
        as={allIcons.HiTrash}
        onClick={() => setList((c) => c.filter((_, i) => i !== idx))}
      />
    </Flex>
  );
};

const ManageQuickActions = () => {
  const theme = useSelector((state: TState) => state.theme);
  const rowData = useSelector((state: TState) => state.rowData);
  const isAdmin = useSelector((state: TState) => state.isAdmin);

  const { getActionsList, onClose, setModalType, setRowData } = useActions();
  const { PageTree, selected, setSelected, checkAll } = usePageTree(
    false,
    true,
  );
  const { RolesInput, roles, handleSetRoles } = useRolesSelect(
    'Action visible uniquement par',
  );

  const toast = useToast();

  const [list, setList] = useState<{ name_id: string; value: any }[]>(
    rowData?.list || [EMPTY_ACTION],
  );

  const actionNameRef = useRef<any>();

  useEffect(() => {
    if (!rowData.pages) {
      checkAll();
    } else {
      setSelected(
        Object.fromEntries(rowData.pages.map((r: string) => [r, true])),
      );
    }

    handleSetRoles(rowData.roles);
  }, [rowData]);

  const renderQuickActionsForm = (
    <>
      <FormGroup field={{ label: "Nom de l'action" } as TField} />
      <Input ref={actionNameRef} mb={3} defaultValue={rowData.name} />
      <Text fontSize={theme.fontSize.small} fontWeight='450'>
        Actions:
      </Text>
      {list.map((_, idx) => (
        <Action list={list} setList={setList} idx={idx} />
      ))}
      <CirlceIcon
        onClick={() => setList((c) => [...c, EMPTY_ACTION])}
        as={allIcons.HiPlus}
        mb={3}
      />
      <Box mb={2}>{RolesInput}</Box>
      {PageTree}
    </>
  );

  const formatedSelected = entries(selected).reduce(
    (prev, [k, v]) => (v ? [...prev, k] : prev),
    [] as string[],
  );
  const formatedRoles = roles.map((r) => r.value);

  const handleSubmit = async () => {
    if (!list[0]?.name_id) {
      toast({ title: "Votre liste d'action est vide", status: 'error' });
      return;
    }

    const resp = await addActionsList({
      name: actionNameRef.current.value,
      list,
      pages: formatedSelected,
      roles: formatedRoles,
    });

    setRowData(resp.data);
    setModalType('editQuickActions');
    getActionsList();
  };

  const handleEdit = async () => {
    if (!list[0]?.name_id) {
      toast({ title: "Votre liste d'action est vide", status: 'error' });
      return;
    }

    await updateActionsList(rowData._id, {
      name: actionNameRef.current.value,
      list,
      pages: formatedSelected,
      roles: formatedRoles,
    });

    getActionsList();
  };

  const handleDelete = async () => {
    await deleteActionsList(rowData._id);
    getActionsList();
    onClose();
  };

  const renderDeleteEntry = <Text>Confirmer la suppression ?</Text>;
  const deletePermission = isAdmin;

  const modalOptions = {
    addQuickActions: {
      component: renderQuickActionsForm,
      action: handleSubmit,
      title: 'Ajouter actions rapide',
      actionLabel: 'Sauvegarder',
    },
    editQuickActions: {
      component: renderQuickActionsForm,
      canDelete: deletePermission ? 'deleteQuickActions' : '',
      action: handleEdit,
      title: 'Editer actions rapide',
      actionLabel: 'Sauvegarder',
    },
    deleteQuickActions: {
      component: renderDeleteEntry,
      action: handleDelete,
      title: "Supprimer l'action",
      actionLabel: 'Confirmer',
    },
  };

  return <Modal options={modalOptions} />;
};

export default ManageQuickActions;
